import Vue from 'vue'

/**
 * 优化的消息总线实现
 * 包含：事件监听器管理、消息队列、错误处理、调试功能
 */
class EnhancedBus {
  constructor() {
    this.bus = new Vue()
    this.listeners = new Map() // 存储所有事件监听器，便于管理
    this.messageQueue = [] // 消息队列
    this.maxQueueSize = 1000 // 最大队列大小
    this.isProcessingQueue = false // 队列处理标志
    this.debugMode = process.env.NODE_ENV === 'development' // 调试模式
    this.eventHistory = [] // 事件历史记录
    this.maxHistorySize = 100 // 最大历史记录大小
  }

  /**
   * 监听事件
   * @param {string} event 事件名
   * @param {Function} callback 回调函数
   * @param {Object} options 选项 {once: boolean, priority: number}
   */
  on(event, callback, options = {}) {
    if (typeof callback !== 'function') {
      console.error(`Bus.on: callback must be a function for event "${event}"`)
      return this
    }

    // 存储监听器信息
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    
    const listenerId = this._generateListenerId()
    const listenerInfo = {
      id: listenerId,
      callback,
      options
    }
    
    this.listeners.get(event).add(listenerInfo)
    
    // 调用原始Vue的$on
    this.bus.$on(event, callback)
    
    if (this.debugMode) {
      this._logEvent('on', event, { listenerId, once: options.once })
    }
    
    return listenerId // 返回监听器ID，便于后续精确移除
  }

  /**
   * 监听一次事件
   * @param {string} event 事件名
   * @param {Function} callback 回调函数
   */
  once(event, callback) {
    if (typeof callback !== 'function') {
      console.error(`Bus.once: callback must be a function for event "${event}"`)
      return this
    }
    
    const listenerId = this._generateListenerId()
    
    // 包装回调，执行后自动移除
    const wrappedCallback = (...args) => {
      try {
        callback(...args)
      } catch (error) {
        this._handleError(event, error, args)
      } finally {
        this.off(event, wrappedCallback)
      }
    }
    
    this.on(event, wrappedCallback, { once: true })
    
    return listenerId
  }

  /**
   * 移除事件监听器
   * @param {string} event 事件名
   * @param {Function|string} callback 回调函数或监听器ID
   */
  off(event, callback) {
    // 如果提供了监听器ID，精确移除
    if (typeof callback === 'string') {
      if (this.listeners.has(event)) {
        const eventListeners = this.listeners.get(event)
        let targetListener = null
        
        for (const listener of eventListeners) {
          if (listener.id === callback) {
            targetListener = listener
            break
          }
        }
        
        if (targetListener) {
          eventListeners.delete(targetListener)
          this.bus.$off(event, targetListener.callback)
          
          if (this.debugMode) {
            this._logEvent('off', event, { listenerId: callback })
          }
        }
        
        // 如果事件没有监听器了，清理Map
        if (eventListeners.size === 0) {
          this.listeners.delete(event)
        }
      }
      return this
    }
    
    // 传统的基于回调函数的移除
    if (event && callback) {
      // 清理我们的监听器Map
      if (this.listeners.has(event)) {
        const eventListeners = this.listeners.get(event)
        const listenersToRemove = []
        
        for (const listener of eventListeners) {
          if (listener.callback === callback) {
            listenersToRemove.push(listener)
          }
        }
        
        for (const listener of listenersToRemove) {
          eventListeners.delete(listener)
          if (this.debugMode) {
            this._logEvent('off', event, { listenerId: listener.id })
          }
        }
        
        if (eventListeners.size === 0) {
          this.listeners.delete(event)
        }
      }
      
      this.bus.$off(event, callback)
    } else if (event) {
      // 移除某事件的所有监听器
      this.listeners.delete(event)
      this.bus.$off(event)
      
      if (this.debugMode) {
        this._logEvent('off', event, { all: true })
      }
    } else {
      // 移除所有事件的所有监听器
      this.listeners.clear()
      this.bus.$off()
      
      if (this.debugMode) {
        this._logEvent('off', 'all', { all: true })
      }
    }
    
    return this
  }

  /**
   * 触发事件
   * @param {string} event 事件名
   * @param  {...any} args 传递的参数
   */
  emit(event, ...args) {
    if (this.debugMode) {
      this._logEvent('emit', event, { argsCount: args.length })
      this._addToHistory(event, args)
    }
    
    try {
      // 立即触发事件
      this.bus.$emit(event, ...args)
    } catch (error) {
      this._handleError(event, error, args)
    }
    
    return this
  }

  /**
   * 异步触发事件（放入队列）
   * @param {string} event 事件名
   * @param  {...any} args 传递的参数
   */
  emitAsync(event, ...args) {
    if (this.messageQueue.length >= this.maxQueueSize) {
      console.warn(`Bus queue overflow for event "${event}", removing oldest message`)
      this.messageQueue.shift() // 移除最旧的消息
    }
    
    this.messageQueue.push({ event, args })
    
    if (!this.isProcessingQueue) {
      this._processMessageQueue()
    }
    
    return this
  }

  /**
   * 批量移除组件的所有事件监听器
   * @param {string[]} eventNames 事件名数组
   */
  removeAllListeners(eventNames = null) {
    if (!eventNames) {
      // 移除所有事件
      this.listeners.forEach((_, event) => {
        this.off(event)
      })
    } else {
      // 移除指定事件
      eventNames.forEach(event => {
        this.off(event)
      })
    }
    return this
  }

  /**
   * 获取某事件的监听器数量
   * @param {string} event 事件名
   * @returns {number} 监听器数量
   */
  listenerCount(event) {
    if (!this.listeners.has(event)) return 0
    return this.listeners.get(event).size
  }

  /**
   * 获取所有监听的事件名
   * @returns {string[]} 事件名数组
   */
  eventNames() {
    return Array.from(this.listeners.keys())
  }

  /**
   * 清空消息队列
   */
  clearQueue() {
    this.messageQueue = []
    return this
  }

  /**
   * 启用/禁用调试模式
   * @param {boolean} enabled 是否启用
   */
  setDebugMode(enabled) {
    this.debugMode = enabled
    return this
  }

  /**
   * 获取事件历史记录
   * @param {number} limit 限制数量
   * @returns {Array} 历史记录
   */
  getEventHistory(limit = 50) {
    return this.eventHistory.slice(-limit)
  }

  /**
   * 清空事件历史
   */
  clearHistory() {
    this.eventHistory = []
    return this
  }

  /**
   * 生成监听器ID
   * @private
   */
  _generateListenerId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }

  /**
   * 记录事件日志
   * @private
   */
  _logEvent(type, event, details = {}) {
    const timestamp = new Date().toISOString()
    console.log(`[Bus] ${timestamp} ${type} "${event}"`, details)
  }

  /**
   * 处理错误
   * @private
   */
  _handleError(event, error, args) {
    console.error(`[Bus] Error in event "${event}" handler:`, error, { args })
    // 可以在这里添加错误上报逻辑
  }

  /**
   * 处理消息队列
   * @private
   */
  _processMessageQueue() {
    if (this.isProcessingQueue || this.messageQueue.length === 0) return
    
    this.isProcessingQueue = true
    
    setTimeout(() => {
      if (this.messageQueue.length > 0) {
        const message = this.messageQueue.shift()
        this.emit(message.event, ...message.args)
      }
      
      this.isProcessingQueue = false
      
      // 继续处理队列中的下一条消息
      if (this.messageQueue.length > 0) {
        this._processMessageQueue()
      }
    }, 0) // 在下一个事件循环中处理
  }

  /**
   * 添加事件到历史记录
   * @private
   */
  _addToHistory(event, args) {
    const record = {
      event,
      timestamp: Date.now(),
      argsCount: args.length
    }
    
    this.eventHistory.push(record)
    
    // 保持历史记录大小在限制内
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift()
    }
  }
}

// 创建单例实例
const Bus = new EnhancedBus()

// 兼容原有Vue实例的$on, $off, $emit, $once方法
// 这样原有的代码不需要改动
Bus.$on = Bus.on.bind(Bus)
Bus.$off = Bus.off.bind(Bus)
Bus.$emit = Bus.emit.bind(Bus)
Bus.$once = Bus.once.bind(Bus)

export default Bus




// WEBPACK FOOTER //
// ./src/libs/bus.js